import { Injectable, type Provider } from '@angular/core';

import { err, makeError, ok, type ApiResult } from '../../../core/models/api-result';
import type { HubCard, HubCardFace } from './hub-card.models';
import { HubCardService } from './hub-card.service';
import type { HubChip, HubFeedItem } from './hub-feed.models';
import { PREVIEW_JOINED_HUBS } from './hub-page-preview.service';

/**
 * HUB CARDS, WITHOUT A SESSION — the harness behind "tell another hub" on
 * `/preview/hubs` (bead sb82.21).
 *
 * Same bargain as the other preview services: only the network seams are
 * overridden, so the dedupe, the caps, the optimistic join and its rollback
 * are the real service's.
 *
 * THE CAST.
 *  · Employee Experience Lab (`?hub=lab`) carries two card posts: Dean
 *    telling it about Padel Club (open, the viewer is not in it, so Join),
 *    and Priya's card for a hub that has since closed (unavailable).
 *  · A share from anywhere adds a real-looking post to each destination, at
 *    the top of that hub's stream and of Everything; Undo takes it away.
 *  · `?cardjoin=fail` makes every join refuse, to show the rollback.
 *
 * ⚠ THE SEEDED POSTS ARE ON HUB PAGES ONLY, never in Everything: that corpus
 * is the prototype's, transcribed verbatim, and specs pin its order and size.
 */

const BASE = '2026-08-22T07:30:00.000Z';

/** What a card shows for each harness hub. Names and counts match the corpus. */
const CARD_DETAILS: Record<string, { purpose: string; cover: string | null; faces: readonly string[] }> = {
  padel: {
    purpose: 'Doubles after work on Tuesdays and Thursdays. All levels; rackets to borrow.',
    cover: '/assets/hubs-preview/snap-roof-terrace.jpg',
    faces: ['p1', 'p2', 'p3', 'p5', 'p8'],
  },
  coffee: {
    purpose: 'Beans, grinders and the machine on Level 2. Who is roasting this week.',
    cover: '/assets/hubs-preview/snap-coffee-queue.jpg',
    faces: ['p4', 'p6', 'p7'],
  },
  lab: {
    purpose: 'Trying new ways to work in the office, and telling you what we learned.',
    cover: '/assets/hubs-preview/pro-lab.jpg',
    faces: ['p1', 'p9', 'p8', 'p2'],
  },
  run: {
    purpose: 'Lunchtime 5k loops and the Sunday ride. Every pace.',
    cover: '/assets/hubs-preview/snap-charity-run.jpg',
    faces: ['p5', 'p10'],
  },
  photo: {
    purpose: 'A theme a month, a walk a quarter, and the best shots on the screens.',
    cover: null,
    faces: ['p7'],
  },
};

const FACE_NAMES: Record<string, string> = {
  p1: 'Dean Okafor',
  p2: 'Priya Raman',
  p3: 'Marcus Webb',
  p4: 'Sofia Almeida',
  p5: 'Sam Ellis',
  p6: 'Aisha Bello',
  p7: 'Tom Bright',
  p8: 'Anya Kaur',
  p9: 'Joel Hartley',
  p10: 'Nina Costa',
};

/** A hub the viewer cannot read any more: only its id and name come back. */
const GONE: HubCard = { hubId: 'offsite-2025', name: 'Leadership Offsite 2025', unavailable: true };

/** Posts the harness viewer shared this page's life, newest first. */
const SHARED: HubFeedItem[] = [];

function faces(ids: readonly string[]): HubCardFace[] {
  return ids.map(id => ({ _id: id, name: FACE_NAMES[id] ?? 'A colleague', picture: null }));
}

/** The card for `hubId`, read "live" against the harness's memberships. */
export function previewHubCard(chip: HubChip | null | undefined, hubId: string): HubCard {
  if (!chip) return { ...GONE, hubId, name: GONE.name };
  const details = CARD_DETAILS[hubId];
  const member = !!chip.memberRole || PREVIEW_JOINED_HUBS.has(hubId);
  return {
    hubId,
    name: chip.name,
    unavailable: false,
    purpose: details?.purpose ?? null,
    coverUrl: details?.cover ?? null,
    primaryColor: chip.primaryColor ?? null,
    logo: chip.logo ?? null,
    memberCount: (chip.memberCount ?? 0) + (PREVIEW_JOINED_HUBS.has(hubId) && !chip.memberRole ? 1 : 0),
    facesSample: faces(details?.faces ?? []),
    viewerIsMember: member,
    canJoin: !member,
    joinMode: 'OPEN',
  };
}

function cardPost(
  id: string,
  hub: HubChip,
  author: { _id: string; firstName: string; lastName: string },
  body: string | null,
  title: string,
  card: HubCard,
  publishedAt: string,
): HubFeedItem {
  return {
    id,
    kind: 'POST',
    format: 'UPDATE',
    source: 'COMMUNITY',
    questionId: null,
    title,
    body,
    publishedAt,
    readTimeMinutes: null,
    pinned: false,
    viewerStarred: false,
    commentCount: 0,
    viewCount: 0,
    shareCount: 0,
    topics: [],
    hub,
    author: { ...author, jobTitle: null, picture: null },
    media: null,
    reactions: { count: 0, viewerHasReacted: false, viewerReaction: null },
    poll: null,
    viewerVotedOptionIds: [],
    link: null,
    hubCard: card,
  };
}

async function chips(): Promise<Map<string, HubChip>> {
  const { HUBS_PREVIEW_ITEMS } = await import('./hubs-preview.fixtures');
  const out = new Map<string, HubChip>();
  for (const item of HUBS_PREVIEW_ITEMS) if (item.hub && !out.has(item.hub._id)) out.set(item.hub._id, item.hub);
  return out;
}

/** The seeded posts for one hub's page, with their cards read now. */
async function seeded(hubId: string): Promise<HubFeedItem[]> {
  if (hubId !== 'lab') return [];
  const all = await chips();
  const lab = all.get('lab');
  if (!lab) return [];
  return [
    cardPost(
      'card-lab-padel',
      lab,
      { _id: 'dean-okafor', firstName: 'Dean', lastName: 'Okafor' },
      'Padel is back on Tuesdays and Thursdays. Rackets to borrow, and nobody minds if you have never played.',
      'Padel is back on Tuesdays and Thursdays',
      previewHubCard(all.get('padel'), 'padel'),
      BASE,
    ),
    cardPost(
      'card-lab-gone',
      lab,
      { _id: 'priya-raman', firstName: 'Priya', lastName: 'Raman' },
      'Notes from the offsite are in here, for anyone who was there.',
      'Notes from the offsite',
      GONE,
      '2026-08-21T16:00:00.000Z',
    ),
  ];
}

/**
 * The hub-card posts a harness read should carry: the seeded ones on their
 * hub's page, and whatever was shared this session wherever it went (and in
 * Everything). Cards are re-read, so a join shows on the next load.
 */
export async function previewHubCardPosts(hubId: string | null | undefined): Promise<HubFeedItem[]> {
  const all = await chips();
  const reread = (item: HubFeedItem): HubFeedItem =>
    item.hubCard && !item.hubCard.unavailable
      ? { ...item, hubCard: previewHubCard(all.get(item.hubCard.hubId), item.hubCard.hubId) }
      : item;
  const shared = SHARED.filter(p => !hubId || p.hub?._id === hubId).map(reread);
  return [...shared, ...(hubId ? await seeded(hubId) : [])];
}

/** One card post by id, for the post page opened from a link. */
export async function previewHubCardPost(id: string): Promise<HubFeedItem | null> {
  const lab = await seeded('lab');
  return [...SHARED, ...lab].find(p => p.id === id) ?? null;
}

let sequence = 0;

@Injectable()
export class HubCardPreviewService extends HubCardService {
  protected override async sendCreate(input: Record<string, unknown>): Promise<ApiResult<{ _id: string } | null>> {
    const all = await chips();
    const destination = all.get(String(input['communityId']));
    const attachments = (input['attachments'] as { hubId: string }[] | undefined) ?? [];
    const hubId = attachments[0]?.hubId ?? '';
    if (!destination) return err(makeError('NOT_FOUND', 'Hub not found'));
    if (destination._id === hubId) {
      return err(makeError('VALIDATION', 'A hub card announces another hub. This post is already in that one.'));
    }
    const id = `card-shared-${++sequence}`;
    const body = typeof input['body'] === 'string' ? input['body'] : null;
    const title = body ?? (typeof input['title'] === 'string' ? input['title'] : 'Update');
    SHARED.unshift(
      cardPost(
        id,
        destination,
        { _id: 'preview-viewer', firstName: 'You', lastName: '' },
        body,
        title,
        previewHubCard(all.get(hubId), hubId),
        new Date(Date.parse('2026-08-22T09:00:00.000Z') - sequence * 1000).toISOString(),
      ),
    );
    return ok({ _id: id });
  }

  protected override async sendDelete(id: string): Promise<ApiResult<boolean | null>> {
    const at = SHARED.findIndex(p => p.id === id);
    if (at < 0) return err(makeError('NOT_FOUND', 'Post not found'));
    SHARED.splice(at, 1);
    return ok(true);
  }

  protected override async sendJoin(hubId: string, _postId: string): Promise<ApiResult<boolean | null>> {
    // A beat, so the optimistic Joined is visible before the answer lands.
    await new Promise(resolve => setTimeout(resolve, 300));
    if (typeof location !== 'undefined' && /[?&]cardjoin=fail\b/.test(location.href)) {
      return err(makeError('SERVER', 'You could not join this hub just now. Try again.', { retryable: true }));
    }
    if (hubId === GONE.hubId) return err(makeError('NOT_FOUND', 'Hub not found'));
    PREVIEW_JOINED_HUBS.add(hubId);
    return ok(true);
  }
}

export const HUB_CARD_PREVIEW_PROVIDER: Provider = {
  provide: HubCardService,
  useClass: HubCardPreviewService,
};
